/**
 * The editor's session, held in one place.
 *
 * Pressentia issues its own identities. The dashboard signs in against the Pressentia API, stores the
 * token it returns, and presents it on every later request — one service, one user list.
 *
 * `localStorage`, not memory: an editor auditing a broadcast opens several tabs of the same
 * stream, and a session that does not survive that is a session they stop using.
 */

const TOKEN_KEY = 'pressentia.session.token';
const EDITOR_KEY = 'pressentia.session.editor';

export type SessionEditor = {
  name: string;
  email: string;
};

type Listener = () => void;

const listeners = new Set<Listener>();

export function readToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private-mode Safari throws on access rather than returning null.
    return null;
  }
}

/**
 * The parsed editor, cached against the raw string it came from.
 *
 * `useSyncExternalStore` compares snapshots with `Object.is`, so a reader that parses JSON on
 * every call hands React a new object every render and it re-renders forever — React error #185,
 * "maximum update depth exceeded", which is what signing in used to do. The cache is what makes
 * this function a legal snapshot: same stored string, same object identity.
 */
let cachedEditorRaw: string | null = null;
let cachedEditor: SessionEditor | null = null;

export function readEditor(): SessionEditor | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(EDITOR_KEY);
  } catch {
    return null;
  }

  if (raw === cachedEditorRaw) return cachedEditor;

  cachedEditorRaw = raw;
  cachedEditor = parseEditor(raw);
  return cachedEditor;
}

function parseEditor(raw: string | null): SessionEditor | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { name, email } = parsed as Partial<SessionEditor>;
    if (typeof name !== 'string' || typeof email !== 'string') return null;
    return { name, email };
  } catch {
    return null;
  }
}

export function startSession(token: string, editor: SessionEditor): void {
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
    window.localStorage.setItem(EDITOR_KEY, JSON.stringify(editor));
  } catch {
    // A session that cannot be stored still works for this tab; the next reload asks again.
  }
  notify();
}

/**
 * Ends the session. Called both by the editor pressing "salir" and by the client on a 401 — a
 * token the API rejects is not worth keeping, and leaving it in place makes every later screen
 * fail with an authorization error instead of showing the sign-in form.
 */
export function endSession(): void {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(EDITOR_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
  notify();
}

export function subscribeToSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(): void {
  for (const listener of listeners) listener();
}
